import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Lock, User, Mail, Shield, Eye, EyeOff, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const AdminSettings = () => {
  const [adminProfile, setAdminProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [commissionRate, setCommissionRate] = useState(20);
  const [newCommissionRate, setNewCommissionRate] = useState('20');
  const [updatingCommission, setUpdatingCommission] = useState(false);
  const [featureFlags, setFeatureFlags] = useState({ bus_booking_visible: false, logistics_visible: false });
  const [togglingFlag, setTogglingFlag] = useState(null);

  useEffect(() => {
    fetchAdminProfile();
    fetchCommissionRate();
    fetchFeatureFlags();
  }, []);

  const fetchAdminProfile = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/admin/settings/profile');
      setAdminProfile(response.data.admin);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const fetchCommissionRate = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/admin/settings/commission-rate');
      const rate = response.data.commissionRate || 0.20;
      const ratePercent = Math.round(rate * 100);
      setCommissionRate(ratePercent);
      setNewCommissionRate(ratePercent.toString());
    } catch (error) {
      console.error('Error fetching commission rate:', error);
      // Default to 20%
      setCommissionRate(20);
      setNewCommissionRate('20');
    }
  };

  const fetchFeatureFlags = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const res = await api.get('/admin/settings/feature-flags');
      setFeatureFlags(res.data);
    } catch (e) {}
  };

  const toggleFlag = async (flag) => {
    setTogglingFlag(flag);
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/admin/settings/feature-flags', { [flag]: !featureFlags[flag] });
      setFeatureFlags(prev => ({ ...prev, [flag]: !prev[flag] }));
      toast({ title: 'Success', description: 'Feature flag updated' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to update flag', variant: 'destructive' });
    } finally {
      setTogglingFlag(null);
    }
  };

  const handleCommissionUpdate = async (e) => {
    e.preventDefault();
    
    const ratePercent = parseFloat(newCommissionRate);
    
    if (isNaN(ratePercent) || ratePercent < 0) {
      toast({
        title: 'Error',
        description: 'Please enter a valid commission rate',
        variant: 'destructive',
      });
      return;
    }

    if (ratePercent > 50) {
      toast({
        title: 'Error',
        description: 'Commission rate cannot exceed 50%',
        variant: 'destructive',
      });
      return;
    }

    setUpdatingCommission(true);

    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.put('/admin/settings/commission-rate', {
        commissionRate: ratePercent / 100  // Convert to decimal
      });

      setCommissionRate(ratePercent);
      toast({
        title: 'Success',
        description: response.data.message || 'Commission rate updated successfully',
      });
    } catch (error) {
      console.error('Error updating commission:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update commission rate',
        variant: 'destructive',
      });
    } finally {
      setUpdatingCommission(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Please fill in all password fields',
        variant: 'destructive',
      });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast({
        title: 'Error',
        description: 'Password must be at least 8 characters long',
        variant: 'destructive',
      });
      return;
    }

    setChangingPassword(true);

    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/admin/settings/change-password', passwordForm);

      toast({
        title: 'Success',
        description: 'Password changed successfully',
      });

      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to change password',
        variant: 'destructive',
      });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your admin account settings</p>
        </div>

        {/* Commission Rate Settings */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Commission Settings</h2>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Current Commission Rate:</strong> {commissionRate}% of each ride/delivery
            </p>
            <p className="text-xs text-blue-600 mt-1">
              This percentage is deducted from drivers and bikers for each completed ride or delivery.
            </p>
          </div>

          <form onSubmit={handleCommissionUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="commissionRate">Commission Rate (%)</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="commissionRate"
                    type="number"
                    step="1"
                    min="0"
                    max="50"
                    placeholder="20"
                    value={newCommissionRate}
                    onChange={(e) => setNewCommissionRate(e.target.value)}
                    disabled={updatingCommission}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                </div>
                <Button
                  type="submit"
                  disabled={updatingCommission || !newCommissionRate}
                  className="bg-pink-600 hover:bg-pink-700"
                >
                  {updatingCommission ? 'Updating...' : 'Update Rate'}
                </Button>
              </div>
              <p className="text-xs text-gray-600">
                Enter the percentage of each ride/delivery fare to be charged as commission (e.g., 20 for 20%)
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>⚠️ Important:</strong> Changing the commission rate will apply to all drivers and bikers immediately and affect all future rides/deliveries.
              </p>
            </div>
          </form>
        </Card>

        {/* Feature Flags */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Landing Page Visibility</h2>
          <p className="text-sm text-gray-500 mb-6">Control which sections are visible to visitors on the landing page.</p>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Bus Booking Section</p>
                <p className="text-sm text-gray-500">"Travel across Zambia by Bus"</p>
              </div>
              <button
                onClick={() => toggleFlag('bus_booking_visible')}
                disabled={togglingFlag === 'bus_booking_visible'}
                className="focus:outline-none"
                data-testid="toggle-bus-booking"
              >
                {featureFlags.bus_booking_visible
                  ? <ToggleRight className="h-8 w-8 text-green-600" />
                  : <ToggleLeft className="h-8 w-8 text-gray-400" />}
              </button>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Logistics Section</p>
                <p className="text-sm text-gray-500">"Tekstar Logistics"</p>
              </div>
              <button
                onClick={() => toggleFlag('logistics_visible')}
                disabled={togglingFlag === 'logistics_visible'}
                className="focus:outline-none"
                data-testid="toggle-logistics"
              >
                {featureFlags.logistics_visible
                  ? <ToggleRight className="h-8 w-8 text-green-600" />
                  : <ToggleLeft className="h-8 w-8 text-gray-400" />}
              </button>
            </div>
          </div>
        </Card>

        {/* Admin Profile Info */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Admin Profile</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                <Shield className="h-8 w-8" />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">{adminProfile?.name}</p>
                <p className="text-sm text-gray-600">{adminProfile?.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
                    Admin
                  </span>
                  {adminProfile?.isActive && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                      Active
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <p className="text-sm text-gray-600">Account Created</p>
                <p className="font-medium text-gray-900">
                  {new Date(adminProfile?.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Last Updated</p>
                <p className="font-medium text-gray-900">
                  {new Date(adminProfile?.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Change Password */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="h-5 w-5 text-gray-700" />
            <h2 className="text-xl font-semibold text-gray-900">Change Password</h2>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>Security Tip:</strong> Use a strong password with at least 8 characters, including uppercase, lowercase, numbers, and special characters.
            </p>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  placeholder="Enter current password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  disabled={changingPassword}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password (min. 8 characters)"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  disabled={changingPassword}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  disabled={changingPassword}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                disabled={changingPassword}
              >
                {changingPassword ? 'Changing Password...' : 'Change Password'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPasswordForm({
                  currentPassword: '',
                  newPassword: '',
                  confirmPassword: ''
                })}
                disabled={changingPassword}
              >
                Clear
              </Button>
            </div>
          </form>
        </Card>

        {/* Security Info */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Security Information</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
              <div>
                <p className="font-medium text-gray-900">Password Protection</p>
                <p className="text-sm text-gray-600">Your password is encrypted and securely stored</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
              <div>
                <p className="font-medium text-gray-900">Role-Based Access</p>
                <p className="text-sm text-gray-600">Admin role provides full access to platform management</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
              <div>
                <p className="font-medium text-gray-900">Session Management</p>
                <p className="text-sm text-gray-600">Sessions expire automatically for security</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;